import { useTranslation, t as tr } from '../i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, List, Typography, Tag, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { userPath } from '../config/paths';

const { Text } = Typography;

// 命令清单由调用方传入（user 端：user/navigation.jsx 的 PALETTE_COMMANDS，与菜单同源）。
// kind 缺省为 page。

const fuzzyMatch = (query, item) => {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;
  const haystack = `${item.label} ${tr(item.label)} ${item.subLabel ? tr(item.subLabel) : ''} ${item.section} ${tr(item.section)} ${(item.keywords || []).join(' ')}`.toLowerCase();
  // 简单包含匹配 + 多关键词 AND
  return q.split(/\s+/).every(token => haystack.includes(token));
};

const CommandPalette = ({ open, onClose, commands = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef();

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // 给 Modal 一点时间挂载后再聚焦
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filtered = useMemo(() => {
    return commands.filter(cmd => fuzzyMatch(query, cmd));
  }, [query, commands]);

  // active idx 不能超出当前 filtered 范围
  useEffect(() => {
    if (activeIdx >= filtered.length) {
      setActiveIdx(0);
    }
  }, [filtered.length, activeIdx]);

  const runCommand = (cmd) => {
    if (!cmd) return;
    if (!cmd.kind || cmd.kind === 'page') {
      navigate(userPath(cmd.path));
    } else if (typeof cmd.onSelect === 'function') {
      cmd.onSelect();
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(idx => Math.min(filtered.length - 1, idx + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(idx => Math.max(0, idx - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runCommand(filtered[activeIdx]);
    }
  };

  // 按区段分组
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((cmd, idx) => {
      if (!map.has(cmd.section)) map.set(cmd.section, []);
      map.get(cmd.section).push({ ...cmd, _idx: idx });
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={620}
      destroyOnClose
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 16, overflow: 'hidden' },
      }}
      style={{ top: 100 }}
      maskClosable
    >
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--glass-border, rgba(0,0,0,0.06))' }}>
        <Input
          ref={inputRef}
          size="large"
          placeholder={t('Search pages or actions… (↑↓ to navigate, Enter to open, Esc to close)')}
          prefix={<SearchOutlined />}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="borderless"
          autoFocus
        />
      </div>
      <div style={{ maxHeight: 460, overflow: 'auto', padding: '8px 0' }}>
        {filtered.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('No matching pages')}
            style={{ padding: 40 }}
          />
        ) : (
          grouped.map(([section, items]) => (
            <div key={section} style={{ marginBottom: 4 }}>
              <div style={{
                padding: '6px 16px 4px',
                fontSize: 11,
                color: 'var(--text-3, rgba(0,0,0,0.45))',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {t(section)}
              </div>
              <List
                dataSource={items}
                renderItem={(cmd) => {
                  const isActive = filtered[activeIdx]?.key === cmd.key;
                  return (
                    <List.Item
                      onMouseEnter={() => setActiveIdx(cmd._idx)}
                      onClick={() => runCommand(cmd)}
                      style={{
                        cursor: 'pointer',
                        padding: '8px 16px',
                        background: isActive ? 'var(--bg-page, #f5f7fa)' : 'transparent',
                        borderLeft: isActive ? '2px solid var(--brand, #2357ff)' : '2px solid transparent',
                        borderBottom: 'none',
                        transition: 'background 0.1s ease',
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                      }}>
                        <span style={{ fontSize: 16, color: isActive ? 'var(--brand, #2357ff)' : 'var(--text-3, rgba(0,0,0,0.55))' }}>
                          {cmd.icon}
                        </span>
                        <span style={{ flex: 1 }}>
                          <Text strong={isActive}>{cmd.subLabel ? `${t(cmd.subLabel)} · ${t(cmd.label)}` : t(cmd.label)}</Text>
                        </span>
                        {isActive && (
                          <Tag style={{ marginRight: 0, fontSize: 11 }}>Enter ↵</Tag>
                        )}
                      </div>
                    </List.Item>
                  );
                }}
              />
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};

export default CommandPalette;
